import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageShell from '../components/chrome/PageShell';
import { useNotifications } from '../context/NotificationContext';
import useMediaQuery from '../utils/useMediaQuery';
import useTopBarHeight from '../hooks/useTopBarHeight';
import FamilyFilter, { FILTER_ROWS } from '../components/notifications/FamilyFilter';
import PushPanel from '../components/notifications/PushPanel';
import NotificationRow from '../components/notifications/NotificationRow';
import { TYPE_LIST } from '../components/notifications/notificationTypes';
import { decorate, groupByDay, fillFor, T, RADIUS } from '../components/notifications/notificationModel';
import openAppLink from '../utils/openAppLink';

const PAGE_SIZE = 20;

/**
 * The notifications page (Sept 2026 redesign, handoff §1): NOTIFICATIONS + the unread line,
 * a family aside (list ≥ 980, pill scroller below), ALL / UNREAD tabs, and the feed grouped
 * by day as separate cards. Filtering is server-side (`?family=` `?unread=`); the page keeps
 * its own list so the bell dropdown's fetch never replaces a filtered page.
 * `?family=` on the URL deep-links a family.
 */
export default function NotificationsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { unreadCount, unreadByFamily, allCount, fetchNotifications, markAsRead, markAllAsRead } = useNotifications();

  const phone = useMediaQuery('(max-width: 639px)');
  const wideAside = useMediaQuery('(min-width: 980px)');
  const headerH = useTopBarHeight();
  const gutter = phone ? 16 : 28;

  const famParam = params.get('family');
  const fam = FILTER_ROWS.some((f) => f.key === famParam) ? famParam : 'all';
  const unreadOnly = params.get('unread') === '1';

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const setFilter = (next) => {
    const p = new URLSearchParams(params);
    if ('family' in next) { if (next.family && next.family !== 'all') p.set('family', next.family); else p.delete('family'); }
    if ('unread' in next) { if (next.unread) p.set('unread', '1'); else p.delete('unread'); }
    setParams(p, { replace: true });
  };

  const load = useCallback(async (pageNum) => {
    setLoading(true);
    const r = await fetchNotifications(pageNum, PAGE_SIZE, { family: fam, unread: unreadOnly }, { keep: false });
    setLoading(false);
    setLoaded(true);
    if (!r) return;
    setItems((prev) => (pageNum === 1 ? r.notifications : [...prev, ...r.notifications]));
    setHasMore(r.hasMore);
    setPage(pageNum);
  }, [fetchNotifications, fam, unreadOnly]);

  useEffect(() => { load(1); }, [load]);

  const readHere = useCallback((id) => {
    const row = items.find((n) => n.id === id);
    if (!row || row.isRead) return;
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    markAsRead(id, row.family);
  }, [items, markAsRead]);

  const openRow = (n) => {
    readHere(n.id);
    if (n.actionUrl) openAppLink(navigate, n.actionUrl);
  };

  const markAll = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    await markAllAsRead();
  };

  const now = useMemo(() => new Date(), [items]); // eslint-disable-line react-hooks/exhaustive-deps
  const days = useMemo(() => groupByDay(items.map((n) => decorate(n, now)), now), [items, now]);
  const counts = { ...unreadByFamily, all: unreadCount };
  const tabUnread = fam === 'all' ? unreadCount : (unreadByFamily[fam] || 0);
  const famLabel = FILTER_ROWS.find((f) => f.key === fam)?.label || '';

  const tab = (label, on, onClick) => (
    <button
      key={label}
      type="button"
      onClick={onClick}
      aria-pressed={on}
      style={{ height: 40, padding: '0 18px', border: '2px solid transparent', borderRadius: RADIUS.pill, background: on ? fillFor('on') : T.white, color: on ? '#fff' : T.ink, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', whiteSpace: 'nowrap', boxSizing: 'border-box' }}
    >
      {label}
    </button>
  );

  return (
    <PageShell bare>
      <style>{PAGE_CSS}</style>
      <main style={{ maxWidth: 1280, margin: '0 auto', padding: phone ? '20px 16px 64px' : '32px 28px 80px', boxSizing: 'border-box', color: T.ink }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, paddingBottom: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <h1 style={{ margin: 0, fontSize: phone ? 22 : 28, fontWeight: 700, letterSpacing: '.1em', lineHeight: 1 }}>NOTIFICATIONS</h1>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, letterSpacing: '.12em', color: T.grey500 }}>
              <span className="pr-notif-pulse" style={{ width: 8, height: 8, borderRadius: '50%', background: T.live }} aria-hidden />
              <span style={{ color: T.ink, fontWeight: 700 }}>{unreadCount.toLocaleString()} UNREAD</span>
              {' · '}{allCount.toLocaleString()} TOTAL
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={markAll} className="pr-notif-primary" style={{ height: 42, padding: '0 20px', border: 0, borderRadius: RADIUS.pill, background: T.ink, color: '#fff', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer' }}>
              MARK ALL READ
            </button>
            <button type="button" onClick={() => navigate('/notifications/settings')} className="pr-notif-ghost" style={{ height: 42, padding: '0 20px', border: 0, borderRadius: RADIUS.pill, background: T.white, color: T.ink, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
              SETTINGS
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: wideAside ? '280px minmax(0,1fr)' : 'minmax(0,1fr)', gap: 24, alignItems: 'start' }}>
          <aside style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, position: wideAside ? 'sticky' : 'static', top: headerH + 24 }}>
            <FamilyFilter active={fam} counts={counts} onSelect={(key) => setFilter({ family: key })} wide={wideAside} gutter={gutter} />
            {wideAside && (
              <>
                <PushPanel />
                <a href="/notifications/settings" onClick={(e) => { e.preventDefault(); navigate('/notifications/settings'); }} style={{ padding: '0 8px', fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: T.grey500, textDecoration: 'none' }}>
                  ALL {TYPE_LIST.length} NOTIFICATION TYPES →
                </a>
              </>
            )}
          </aside>

          <section style={{ display: 'flex', flexDirection: 'column', gap: 24, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {tab('ALL', !unreadOnly, () => setFilter({ unread: false }))}
                {tab(`UNREAD · ${tabUnread}`, unreadOnly, () => setFilter({ unread: true }))}
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: T.grey500 }}>
                {fam === 'all' ? 'EVERYTHING' : `FILTERED · ${famLabel}`}
              </span>
            </div>

            {!wideAside && <PushPanel />}

            {loaded && days.length === 0 && !loading && (
              <div style={{ padding: '56px 24px', borderRadius: RADIUS.panel, background: T.white, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
                <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.14em' }}>INBOX IS EMPTY.</span>
                <span style={{ fontSize: 12, color: T.grey500 }}>Go outside. Someone will open a portal.</span>
              </div>
            )}

            {days.map((d) => (
              <div key={d.label} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.12em', whiteSpace: 'nowrap' }}>{d.label}</span>
                  <span style={{ fontSize: 11, letterSpacing: '.1em', color: T.grey500, whiteSpace: 'nowrap' }}>{d.count}</span>
                  <span style={{ flex: 1, height: 2, background: T.ink }} aria-hidden />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {d.items.map((n) => (
                    <NotificationRow key={n.id} n={n} phone={phone} onOpen={openRow} onRead={readHere} />
                  ))}
                </div>
              </div>
            ))}

            {!loaded && loading && (
              <div style={{ padding: '40px 0', textAlign: 'center', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', color: T.grey500 }}>LOADING…</div>
            )}

            {hasMore && (
              <button type="button" onClick={() => load(page + 1)} disabled={loading} className="pr-notif-ghost" style={{ alignSelf: 'center', height: 42, padding: '0 22px', border: 0, borderRadius: RADIUS.pill, background: T.white, fontFamily: 'inherit', fontSize: 11, fontWeight: 700, letterSpacing: '.14em', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
                {loading ? 'LOADING…' : 'LOAD OLDER…'}
              </button>
            )}
          </section>
        </div>
      </main>
    </PageShell>
  );
}

/** Hover states + the one pulse. Motion is off entirely under reduced motion. */
const PAGE_CSS = `
  .pr-notif-ghost:hover { background: #000 !important; color: #fff !important; }
  .pr-notif-primary:hover, .pr-notif-action:hover { opacity: .8; }
  .pr-push-pill:hover { background: #000 !important; color: #fff !important; }
  .pr-family-row:hover:not([aria-pressed="true"]) { background: #F5F5F5 !important; }
  .pr-noscroll::-webkit-scrollbar { display: none; }
  @keyframes prNotifPulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .35; transform: scale(.7); } }
  .pr-notif-pulse { animation: prNotifPulse 1.6s ease-in-out infinite; }
  @media (prefers-reduced-motion: reduce) { .pr-notif-pulse { animation: none; } }
`;
